import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { XxsGapCol } from '@xing-com/crate-jobs-components-columns';
import {
  JobTeaserFacts,
  useAriaLabel,
} from '@xing-com/crate-jobs-components-job-teaser-options';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import { useJobTeaserClickExternalTracking } from '@xing-com/crate-jobs-hooks';
import type { JobTeaserCardProps } from '@xing-com/crate-jobs-xinglets';

import * as S from './job-teaser-card.styles';

export const JobTeaserCard: React.FC<JobTeaserCardProps> = ({
  job,
  isNew,
  url,
  target = '_blank',
  matchingHighlights,
  actions,
  onClick: clickCallback,
  onContextMenu,
  onAuxClick,
  'data-testid': dataTestId,
  headingLevel = 'h3',
  profileImageSize = 'xsmall',
  profileImageSizeConfined = 'small',
  className,
  'aria-label': ariaLabel,
  hasTopButton = false,
}) => {
  const {
    activeUntil,
    id,
    refreshedAt,
    title,
    location,
    companyName,
    companyLogo,
    redirectsToThirdPartyUrl,
    applyUrl,
  } = selectJobData(job);

  const ariaLabelText = useAriaLabel({
    ariaLabel: ariaLabel ?? title,
    redirectsToThirdPartyUrl,
    target,
  });
  const onClick = useJobTeaserClickExternalTracking({
    isExternalJob: redirectsToThirdPartyUrl,
    jobId: id,
    url,
    clickCallback,
  });

  return (
    <S.Card
      forwardedAs="article"
      href={redirectsToThirdPartyUrl && applyUrl ? applyUrl : url}
      linkProps={{
        target,
        'aria-label': ariaLabelText,
        onClick,
        onContextMenu,
        onAuxClick,
        rel: redirectsToThirdPartyUrl
          ? 'noopener nofollow sponsored'
          : undefined,
      }}
      data-testid={dataTestId}
      className={className}
    >
      <S.HeaderContainer>
        <S.TopRow>
          <S.Image
            type="company"
            profileName={companyName}
            size={profileImageSize}
            sizeConfined={profileImageSizeConfined}
            src={companyLogo ?? ''}
            // @ts-expect-error FIXME: SC6
            title={null}
            alt=""
            aria-hidden
          />
          {redirectsToThirdPartyUrl ? (
            <S.TopHighlight variant="default" $hasTopButton={hasTopButton}>
              <AriaExtendedText
                item={{ id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT' }}
                a11yItem={{
                  id: 'JOBS_JOB_TEASER_EXTERNAL_JOB_HIGHLIGHT_A11Y',
                }}
              />
            </S.TopHighlight>
          ) : matchingHighlights?.highlight ? (
            <S.TopHighlight variant="highlight" $hasTopButton={hasTopButton}>
              {matchingHighlights?.highlight?.localization.localizationValue}
            </S.TopHighlight>
          ) : null}
        </S.TopRow>

        <S.Details>
          <XxsGapCol>
            <S.Title
              forwardedAs={headingLevel}
              size="medium"
              data-testid="job-teaser-card-title"
              noMargin
            >
              {title}
            </S.Title>

            {location?.city ? (
              <S.City size="medium" lineHeight="dense" noMargin>
                {location.city}
              </S.City>
            ) : null}

            <S.Company
              data-testid="job-teaser-card-company"
              size="medium"
              lineHeight="dense"
              noMargin
            >
              {companyName}
            </S.Company>
          </XxsGapCol>
          <JobTeaserFacts job={job} matchingHighlights={matchingHighlights} />
        </S.Details>
        <S.EndRow>
          {refreshedAt ? (
            <S.PublicationDate
              activeUntil={activeUntil}
              date={refreshedAt}
              isNew={isNew}
            />
          ) : null}

          {actions}
        </S.EndRow>
      </S.HeaderContainer>
    </S.Card>
  );
};
